import { Component, Inject, OnInit } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { FuseConfirmDialogComponent } from "@fuse/components/confirm-dialog/confirm-dialog.component";
import { FuseProgressBarService } from "@fuse/components/progress-bar/progress-bar.service";
import { WebSocketService } from "cw-backend";
import { finalize } from "rxjs";
import { MedicalProvider } from "../../../../../../../../shared/cw-backend/providers/operational/medical/medical.provider";
import { RequestProvider } from "../../../../../../../../shared/cw-backend/providers/operational/medical/request.provider";
import {
    IcpClearanceTO,
    IntegraIcpService,
} from "../../../../../../../../shared/cw-backend/providers/operational/medical/signature.provider";
import { PrintDocumentComponent } from "../print-document/print-document.component";

type DialogState = "LIST" | "FLOW";

@Component({
    selector: "app-signature-document",
    templateUrl: "./signature-document.component.html",
    styleUrls: ["./signature-document.component.scss"],
})
export class SignatureDocumentComponent implements OnInit {
    state: DialogState = "LIST";

    loading = true;
    errorMsg = "";
    a1ErrorMsg = "";
    signingA1 = false;
    public pdfSrc: string = null;

    clearances: IcpClearanceTO[] = [];
    selectedClearance?: IcpClearanceTO;

    // ==== NOVO: controle de autorização ====
    authDone = false;
    authPolling = false;

    // token opaco que você passa no callback_uri (?option=...)
    optionToken: string | null = null;

    clearanceImages: Record<string, string> = {
        VIDaaS: "assets/clearances/vidaas.png",
        OUTRO_PRODUTO: "assets/clearances/outro.png",
    };

    constructor(
        private dialogRef: MatDialogRef<SignatureDocumentComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private integraIcp: IntegraIcpService,
        private progressBar: FuseProgressBarService,
        private medicalProvider: MedicalProvider,
        private requestProvider: RequestProvider,
        private websocket: WebSocketService,
        public dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.websocket.subscribeCerfificate();

        this.websocket.messageReceived.subscribe((message) => {
            console.log("message received in component", message);

            if (!message?.body) {
                return;
            }

            let payload: {
                status: string;
                credentialId: string;
                secret_data: string;
            };

            try {
                payload = JSON.parse(message.body);
            } catch (e) {
                console.error("Erro ao converter mensagem para JSON", e);
                return;
            }

            if (payload.status === "AUTENTICADO") {
                this.authDone = true;
                this.authPolling = false;

                this.integraIcp
                    .updateParameterCredential(
                        payload.credentialId,
                        this.data.performer.id,
                        payload.secret_data,
                    )
                    .subscribe({
                        next: () => {
                            console.log(
                                "Credential atualizado no parâmetro do profissional",
                            );
                        },
                        error: (err) => {
                            console.error(
                                "Erro ao atualizar credential no backend",
                                err,
                            );
                        },
                    });
            }
        });

        this.loadClearances();
    }

    ngOnDestroy(): void {}

    loadClearances(): void {
        this.loading = true;
        this.errorMsg = "";
        this.a1ErrorMsg = "";
        this.clearances = [];

        this.integraIcp
            .startAuthentication(this.data.performer.id)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (res) => {
                    const apiClearances = res?.body?.clearances ?? [];

                    this.optionToken = res?.body?.option ?? null;

                    const certificadoA1: IcpClearanceTO = {
                        productName: "Certificado A1",
                        providerName: "ClinicAll",
                        clearanceType: "A1",
                        clearanceEndpoint: null,
                    };

                    this.clearances = [certificadoA1, ...apiClearances];
                },
                error: (err) => {
                    console.error(err);
                    this.errorMsg = "Erro ao iniciar autenticação.";
                },
            });
    }

    signCertificateA1(): void {
        this.signWithCertificateA1(() =>
            this.medicalProvider.signedPdf(this.data.document, this.data.performer),
        );
    }

    signCertificateA1ToRequest(): void {
        this.signWithCertificateA1(() => this.requestProvider.signedPdf(this.data.request));
    }

    private async signWithCertificateA1(sign: () => Promise<any>): Promise<void> {
        if (this.signingA1) {
            return;
        }
        this.signingA1 = true;
        this.a1ErrorMsg = "";
        this.progressBar.show();
        try {
            const result = await sign();
            const signedPdf = result?.pdf;
            if (typeof signedPdf !== "string" || !signedPdf.startsWith("JVBERi0")) {
                throw new Error("O PDF assinado não foi retornado em formato válido.");
            }

            const confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
                disableClose: false,
            });
            confirmDialogRef.componentInstance.action = "delete";
            confirmDialogRef.componentInstance.deleteMessage = "Deseja imprimir o documento assinado?";
            confirmDialogRef.afterClosed().subscribe((print) => {
                if (!print) {
                    this.dialogRef.close();
                    return;
                }
                this.pdfSrc = signedPdf;
                const printDialogRef = this.dialog.open(PrintDocumentComponent, {
                    height: "80%",
                    width: "80%",
                    data: { pdf: signedPdf },
                });
                printDialogRef.afterClosed().subscribe(() => {
                    this.pdfSrc = null;
                    this.dialogRef.close();
                });
            });
        } catch (error) {
            this.a1ErrorMsg = error?.error?.message || error?.message || "Não foi possível assinar o PDF com o certificado A1.";
        } finally {
            this.signingA1 = false;
            this.progressBar.hide();
        }
    }

    select(c: IcpClearanceTO): void {
        if (c.clearanceType === "A1") {
            if (this.data.type == "R") {
                this.signCertificateA1ToRequest();
            } else if (this.data.type == "D") {
                this.signCertificateA1();
            }

            return;
        }

        if (c.productName === "VIDaaS") {
            this.verifyAuthenticationStatus(c);
        }
    }

    verifyAuthenticationStatus(c: IcpClearanceTO): void {
        this.integraIcp.getCredentialDetails(this.data.performer.id).subscribe({
            next: (res) => {
                if (res?.body?.ok) {
                    if (this.data.type == "R") {
                        this.signedVidaasRequest();
                    } else if (this.data.type == "D") {
                        this.signedVidaas();
                    }
                } else {
                    this.selectedClearance = c;
                    this.state = "FLOW";
                    this.authDone = false;

                    window.open(
                        c.clearanceEndpoint,
                        "_blank",
                        "noopener,noreferrer",
                    );
                }
                return;
            },
            error: (err) => {
                console.error("Erro ao obter detalhes da credencial", err);
                this.errorMsg = "Erro ao obter detalhes da credencial.";
            },
        });
    }

    signedVidaas(): void {
        this.progressBar.show();
        this.integraIcp
            .signedPdf(this.data.document, this.data.performer)
            .then((x) => {
                this.progressBar.hide();
                this.pdfSrc = x.pdf as string;

                var newBlob = new Blob([x], {
                    type: "application/pdf",
                });

                const nav = window.navigator as any;
                if (nav && nav.msSaveOrOpenBlob) {
                    nav.msSaveOrOpenBlob(newBlob);
                    return;
                }

                const data = window.URL.createObjectURL(newBlob);

                this.progressBar.hide();
                const dialogRef = this.dialog.open(PrintDocumentComponent, {
                    height: "80%",
                    width: "80%",
                    data: { pdf: this.pdfSrc },
                });

                dialogRef.afterClosed().subscribe(() => {
                    this.pdfSrc = null;
                    this.dialogRef.close();
                });

                setTimeout(function () {
                    window.URL.revokeObjectURL(data);
                }, 100);
            });
    }

    signedVidaasRequest(): void {
        this.progressBar.show();
        this.integraIcp.signedRequestPdf(this.data.request).then((x) => {
            this.progressBar.hide();
            this.pdfSrc = x.pdf as string;

            var newBlob = new Blob([x], {
                type: "application/pdf",
            });

            const nav = window.navigator as any;
            if (nav && nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(newBlob);
                return;
            }

            const data = window.URL.createObjectURL(newBlob);

            this.progressBar.hide();
            const dialogRef = this.dialog.open(PrintDocumentComponent, {
                height: "80%",
                width: "80%",
                data: { pdf: this.pdfSrc },
            });

            dialogRef.afterClosed().subscribe(() => {
                this.pdfSrc = null;
                this.dialogRef.close();
            });

            setTimeout(function () {
                window.URL.revokeObjectURL(data);
            }, 100);
        });
    }

    openAgain(): void {
        if (!this.selectedClearance) return;
        window.open(
            this.selectedClearance.clearanceEndpoint,
            "_blank",
            "noopener,noreferrer",
        );
    }

    back(): void {
        this.state = "LIST";
        this.selectedClearance = undefined;
        this.authDone = false;
    }

    close(): void {
        this.dialogRef.close();
    }

    continueAfterAuth(): void {
        const hasDocument = !!this.data?.document;
        const hasRequest = !!this.data?.request;

        if (hasDocument) {
            this.signedVidaas();
        } else if (hasRequest) {
            this.signedVidaasRequest();
        } else {
            console.error(
                "Nenhum dado disponível para assinatura (document ou request).",
            );
        }
    }

    getClearanceImage(productName: string): string {
        return (
            this.clearanceImages[productName] ?? "assets/clearances/default.png"
        );
    }

    observableWebsocket(): void {}
}
